import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { safeText } from '../lib/render';
import Wizard, { FloatingAction } from '../components/Wizard';

export default function WorldBank() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardBusy, setWizardBusy] = useState(false);
  const [wizardErr, setWizardErr] = useState(null);
  const [varsByTopic, setVarsByTopic] = useState({});
  const [countries, setCountries] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/api/worldbank/analyses');
        setItems(res.analyses || []);
      } catch (e) {
        setErr(e instanceof ApiError ? e.message : 'Failed to load analyses');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Lazy-load vars + countries the first time the wizard opens
  useEffect(() => {
    if (!wizardOpen || Object.keys(varsByTopic).length > 0) return;
    (async () => {
      try {
        const [v, c] = await Promise.all([
          api.get('/api/worldbank/variables'),
          api.get('/api/worldbank/countries').catch(() => ({ countries: [] })),
        ]);
        setVarsByTopic(v.variables_by_topic || {});
        setCountries(c.countries || []);
      } catch (e) {
        setWizardErr(e instanceof ApiError ? e.message : 'Failed to load form options');
      }
    })();
  }, [wizardOpen]);

  const countryName = useMemo(() => {
    const m = new Map();
    for (const c of countries) m.set(c.iso3, c.name);
    return m;
  }, [countries]);

  async function submit(state) {
    if (!state.var1Code || !state.var2Code) {
      setWizardErr('Pick both variables.');
      return;
    }
    if (state.var1Code === state.var2Code) {
      setWizardErr('Variables must be different.');
      return;
    }
    setWizardBusy(true);
    setWizardErr(null);
    try {
      const res = await api.post('/api/worldbank/analyze', {
        var1_code: state.var1Code,
        var2_code: state.var2Code,
        country_iso3: state.country,
        country_name: countryName.get(state.country) || state.country,
        years: Number(state.years || 40),
      });
      if (res.task_id) {
        nav(`/worldbank/task/${res.task_id}`);
      } else {
        setWizardErr(res.error || 'No task id returned');
      }
    } catch (e) {
      setWizardErr(e instanceof ApiError ? e.message : 'Failed to start analysis');
    } finally {
      setWizardBusy(false);
    }
  }

  return (
    <>
      <section className="hero-gradient py-5">
        <div className="container py-4">
          <div className="text-center text-white">
            <span className="badge bg-info text-dark px-3 py-2 mb-3" style={{ fontSize: '0.82rem' }}>
              <i className="fas fa-chart-line me-1"></i>Econometrics
            </span>
            <h1 style={{ fontWeight: 800, fontSize: '2.6rem' }}>World Bank time-series</h1>
            <p className="lead" style={{ color: 'rgba(255,255,255,0.75)', maxWidth: 680, margin: '0 auto' }}>
              Cointegration, Granger causality, VAR, impulse-response on any two indicators.
              10 credits per run.
            </p>
          </div>
        </div>
      </section>

      <div className="main-container">
        <div className="d-flex justify-content-between align-items-center flex-wrap mb-4 gap-2">
          <Link to="/dashboard" className="text-muted small">
            <i className="fas fa-arrow-left me-1"></i>Back to dashboard
          </Link>
          <button
            type="button"
            className="btn btn-cta"
            onClick={() => { setWizardErr(null); setWizardOpen(true); }}
          >
            <i className="fas fa-plus me-1"></i>New analysis
          </button>
        </div>

        {err && <div className="alert alert-danger">{err}</div>}
        {loading && <div className="text-muted">Loading analyses…</div>}

        {!loading && items.length === 0 && !err && (
          <div className="card">
            <div className="card-body text-center py-5">
              <i className="fas fa-chart-line fa-3x text-muted mb-3"></i>
              <h5>No analyses yet</h5>
              <p className="text-muted mb-3" style={{ maxWidth: 520, margin: '0 auto' }}>
                Start your first time-series analysis. Each run also generates
                five research topics you can use for a dissertation.
              </p>
              <button type="button" className="btn btn-cta btn-lg" onClick={() => setWizardOpen(true)}>
                <i className="fas fa-plus me-1"></i>Start your first analysis
              </button>
            </div>
          </div>
        )}

        <div className="row g-3">
          {items.map((a) => (
            <div key={a.analysis_id} className="col-lg-6">
              <Link
                to={`/worldbank/${a.analysis_id}`}
                className="card h-100 text-decoration-none text-body"
                style={{ transition: 'transform 0.18s, box-shadow 0.18s' }}
              >
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-start gap-2">
                    <div className="me-2" style={{ minWidth: 0 }}>
                      <div className="fw-semibold">
                        {safeText(a.var1_name)}{' '}
                        <span className="text-muted fw-normal">vs</span> {safeText(a.var2_name)}
                      </div>
                      <div className="text-muted small mt-1">
                        <i className="fas fa-globe-africa me-1"></i>
                        {safeText(a.country_name)} • {safeText(a.start_year)}–{safeText(a.end_year)} • {safeText(a.n_obs)} obs
                      </div>
                    </div>
                    <span className={`status-badge ${safeText(a.status, 'pending').toLowerCase()}`}>
                      {safeText(a.status, 'pending')}
                    </span>
                  </div>
                </div>
              </Link>
            </div>
          ))}
        </div>
      </div>

      <FloatingAction label="New analysis" onClick={() => setWizardOpen(true)} />

      {wizardOpen && (
        <Wizard
          title="New time-series analysis"
          busy={wizardBusy}
          error={wizardErr}
          onClose={() => !wizardBusy && setWizardOpen(false)}
          onSubmit={submit}
          submitLabel="Analyze (10 credits)"
          initialState={{
            var1Code: '',
            var2Code: '',
            country: 'ZMB',
            years: 40,
          }}
          steps={[
            {
              label: 'Variable 1',
              canAdvance: (s) => !!s.var1Code,
              render: ({ state, setState }) => (
                <VariablePickStep
                  state={state}
                  setState={setState}
                  varsByTopic={varsByTopic}
                  fieldKey="var1Code"
                  title="Pick your first variable"
                  subtitle="This is variable 1 in the analysis."
                />
              ),
            },
            {
              label: 'Variable 2',
              canAdvance: (s) => !!s.var2Code && s.var2Code !== s.var1Code,
              render: ({ state, setState }) => (
                <VariablePickStep
                  state={state}
                  setState={setState}
                  varsByTopic={varsByTopic}
                  fieldKey="var2Code"
                  excludeCode={state.var1Code}
                  title="Pick your second variable"
                  subtitle="Must be different from variable 1."
                />
              ),
            },
            {
              label: 'Country',
              canAdvance: (s) => !!s.country,
              render: ({ state, setState }) => (
                <CountryStep state={state} setState={setState} countries={countries} />
              ),
            },
            {
              label: 'Years',
              canAdvance: (s) => s.years >= 10 && s.years <= 60,
              render: ({ state, setState }) => <YearsStep state={state} setState={setState} />,
            },
            {
              label: 'Review',
              canAdvance: () => true,
              render: ({ state }) => (
                <ReviewStep state={state} varsByTopic={varsByTopic} countryName={countryName} />
              ),
            },
          ]}
        />
      )}
    </>
  );
}

function VariablePickStep({ state, setState, varsByTopic, fieldKey, excludeCode, title, subtitle }) {
  const topics = Object.entries(varsByTopic || {});
  const selected = state[fieldKey];
  return (
    <div>
      <h5 className="mb-1">{title}</h5>
      <p className="text-muted small mb-3">{subtitle}</p>
      <select
        className="form-select form-select-lg"
        value={selected || ''}
        onChange={(e) => setState({ ...state, [fieldKey]: e.target.value })}
      >
        <option value="">Select a variable…</option>
        {topics.map(([topic, vars]) => (
          <optgroup key={topic} label={topic}>
            {(vars || []).map((v) => (
              <option key={v.code} value={v.code} disabled={excludeCode && v.code === excludeCode}>
                {v.name}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
    </div>
  );
}

function CountryStep({ state, setState, countries }) {
  return (
    <div>
      <h5 className="mb-1">Pick a country</h5>
      <p className="text-muted small mb-3">
        Default is Zambia. Switch to any country in the World Bank catalogue.
      </p>
      <select
        className="form-select form-select-lg"
        value={state.country}
        onChange={(e) => setState({ ...state, country: e.target.value })}
      >
        {countries.length === 0 && <option value="ZMB">Zambia</option>}
        {countries.map((c) => (
          <option key={c.iso3} value={c.iso3}>{c.name}</option>
        ))}
      </select>
    </div>
  );
}

function YearsStep({ state, setState }) {
  const preset = [20, 30, 40, 50, 60];
  return (
    <div>
      <h5 className="mb-1">How many years?</h5>
      <p className="text-muted small mb-3">
        Longer windows give more statistical power; shorter ones capture recent
        structural breaks more cleanly.
      </p>
      <div className="d-flex flex-wrap gap-2 mb-3">
        {preset.map((p) => (
          <button
            key={p}
            type="button"
            className={`btn ${state.years === p ? 'btn-cta' : 'btn-outline-secondary'}`}
            onClick={() => setState({ ...state, years: p })}
          >
            {p} years
          </button>
        ))}
      </div>
      <input
        type="number"
        className="form-control"
        min={10}
        max={60}
        step={5}
        value={state.years}
        onChange={(e) => {
          const v = Number(e.target.value);
          if (!Number.isFinite(v)) return;
          setState({ ...state, years: Math.max(10, Math.min(60, v)) });
        }}
      />
    </div>
  );
}

function ReviewStep({ state, varsByTopic, countryName }) {
  const flat = Object.values(varsByTopic || {}).flat();
  const nameFor = (code) => (flat.find((v) => v.code === code) || {}).name || code;
  return (
    <div>
      <h5 className="mb-3">Ready to analyze</h5>
      <div className="card" style={{ background: '#fafbfd', border: '1px solid var(--border)' }}>
        <div className="card-body">
          <Row label="Variable 1" value={nameFor(state.var1Code)} />
          <Row label="Variable 2" value={nameFor(state.var2Code)} />
          <Row label="Country" value={countryName.get(state.country) || state.country} />
          <Row label="Years" value={`${state.years} years`} />
        </div>
      </div>
      <div className="alert alert-info small mt-3 mb-0">
        <i className="fas fa-info-circle me-2"></i>
        Analysis typically takes 2–8 minutes. You can close this page —
        results will appear in your list when ready.
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="d-flex gap-3 py-1" style={{ borderBottom: '1px dashed rgba(0,0,0,0.06)' }}>
      <div className="text-muted small" style={{ minWidth: 120 }}>{label}</div>
      <div className="small" style={{ flex: 1, fontWeight: 500 }}>
        {value || <span className="text-muted">—</span>}
      </div>
    </div>
  );
}
